/**
 * Currency selector dropdown for the header
 * Allows users to switch between supported currencies
 * Displays a globe icon that opens a dropdown with currency options
 */

import { useState, useRef } from 'react';
import { Globe, Check, RefreshCw } from 'lucide-react';
import { useCurrency, CURRENCY_CONFIG } from '../../contexts/CurrencyContext';
import { Currency } from '../../types';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useKeyboardNav } from '../../hooks/useKeyboardNav';

interface CurrencySelectorProps {
  /** Additional CSS classes */
  className?: string;
  /** Compact mode for mobile menu */
  compact?: boolean;
}

/**
 * Currency selector dropdown component
 */
export default function CurrencySelector({ className = '', compact = false }: CurrencySelectorProps): JSX.Element {
  const { currency, setCurrency, loading, error, refreshRates } = useCurrency();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useClickOutside(dropdownRef, () => setIsOpen(false));

  // Close dropdown on Escape key
  useKeyboardNav({
    onEscape: () => setIsOpen(false),
    enabled: isOpen,
  });

  /**
   * Handle currency selection
   */
  const handleSelect = (selectedCurrency: Currency) => {
    setCurrency(selectedCurrency);
    setIsOpen(false);
  };

  /**
   * Handle refresh rates
   */
  const handleRefresh = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await refreshRates();
  };

  const currentConfig = CURRENCY_CONFIG[currency];

  // Compact mode for mobile menu
  if (compact) {
    return (
      <div className={`space-y-1 ${className}`}>
        <div className="px-3 py-2 text-sm font-medium text-gray-500 dark:text-gray-400">
          Currency
        </div>
        {Object.values(Currency).map((curr) => {
          const config = CURRENCY_CONFIG[curr];
          const isSelected = curr === currency;
          return (
            <button
              key={curr}
              onClick={() => handleSelect(curr)}
              className={`flex items-center justify-between w-full px-3 py-2.5 rounded-lg transition-colors ${
                isSelected
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <span className="flex items-center gap-3">
                <span className="w-6 text-center font-medium">{config.symbol}</span>
                <span>{config.name}</span>
              </span>
              {isSelected && <Check size={18} />}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-1.5 p-2 rounded-lg transition-colors ${
          isOpen
            ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
        }`}
        aria-label={`Currency: ${currentConfig.name}. Click to change.`}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <Globe size={20} />
        <span className="text-sm font-medium">{currency}</span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden z-50"
          role="listbox"
          aria-label="Select currency"
        >
          {/* Header */}
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Select Currency
            </span>
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors disabled:opacity-50"
              aria-label="Refresh exchange rates"
              title="Refresh rates"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-4 py-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border-b border-gray-200 dark:border-gray-700">
              {error}
            </div>
          )}

          {/* Currency Options */}
          <div className="py-1">
            {Object.values(Currency).map((curr) => {
              const config = CURRENCY_CONFIG[curr];
              const isSelected = curr === currency;
              return (
                <button
                  key={curr}
                  onClick={() => handleSelect(curr)}
                  className={`flex items-center justify-between w-full px-4 py-2.5 transition-colors ${
                    isSelected
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className="flex items-center gap-3">
                    <span className="w-8 text-lg font-medium text-gray-600 dark:text-gray-400">
                      {config.symbol}
                    </span>
                    <span className="flex flex-col items-start">
                      <span className="font-medium">{curr}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {config.name}
                      </span>
                    </span>
                  </span>
                  {isSelected && (
                    <Check size={18} className="text-primary-600 dark:text-primary-400" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Prices converted using live rates
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
